import { useCallback, useMemo, useRef, useState } from "react";

interface PendingValue<V> {
  value: V;
  // 이 값을 고칠 당시의 적용값 — 적용값이 그 뒤 패널 밖에서 바뀌면 이 수정은 버린다.
  base: V;
}

type PendingValues<F> = { [K in keyof F]?: PendingValue<F[K]> };

// 쿼리스트링 왕복으로 number/string이 섞이거나 배열 참조가 바뀌어도 같은 값이면 같다고 본다.
const isSameValue = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// 적용값 위에 아직 유효한(적용값이 그 사이 바뀌지 않은) 수정만 덮어쓴다.
// 덮어쓰지 않은 키는 적용값의 참조를 그대로 유지한다 — 그룹 카드 memo가 이 참조로 리렌더를 건너뛴다.
const resolveDraft = <F extends object>(
  applied: F,
  pending: PendingValues<F>
): F => {
  const next = { ...applied };
  (Object.keys(pending) as (keyof F)[]).forEach((key) => {
    const entry = pending[key];
    if (entry && isSameValue(entry.base, applied[key])) {
      next[key] = entry.value;
    }
  });
  return next;
};

const withPatch = <F extends object>(
  pending: PendingValues<F>,
  patch: Partial<F>,
  applied: F
): PendingValues<F> => {
  const next = { ...pending };
  (Object.keys(patch) as (keyof F)[]).forEach((key) => {
    next[key] = {
      value: patch[key] as F[typeof key],
      base: applied[key],
    };
  });
  return next;
};

// 상세 필터의 드래프트 — `필터 적용`을 누르기 전까지 바꾼 값을 적용값 위에 덮어 보여준다.
//
// 수정한 키만 들고 있다가 그 키의 적용값이 패널 밖에서 바뀌면(칩 삭제·전체 해제·상단 상품 유형 버튼)
// 그 수정은 버리고 새 적용값을 그대로 보여준다 — 동기화 effect 없이 렌더 중에 판정한다.
//
// 세터는 모두 고정 참조다(적용값은 ref로 읽는다). 범위 입력처럼 키마다 드래프트가 바뀌어도
// 토글 콜백 참조가 유지돼야 memo된 그룹 카드(브랜드 목록 등)가 다시 그려지지 않는다.
const usePathPickFilterDraft = <F extends object>(applied: F) => {
  const [pending, setPending] = useState<PendingValues<F>>({});
  // 이벤트 핸들러에서 최신 적용값을 읽기 위한 latest-ref.
  const appliedRef = useRef(applied);
  appliedRef.current = applied;

  const draft = useMemo(
    () => resolveDraft(applied, pending),
    [applied, pending]
  );

  const setDraftValues = useCallback((patch: Partial<F>) => {
    setPending((prev) => withPatch(prev, patch, appliedRef.current));
  }, []);

  // 현재 드래프트에 따라 정해지는 변경(토글 등) — 최신 드래프트를 받아 패치를 돌려준다.
  const updateDraft = useCallback((getPatch: (draft: F) => Partial<F>) => {
    setPending((prev) => {
      const current = resolveDraft(appliedRef.current, prev);
      return withPatch(prev, getPatch(current), appliedRef.current);
    });
  }, []);

  // 적용하지 않은 수정을 모두 버린다 — 취소·패널 닫힘·적용 후에 부른다.
  const discard = useCallback(() => {
    setPending({});
  }, []);

  return { draft, setDraftValues, updateDraft, discard };
};

export default usePathPickFilterDraft;
